/** Shared form styling for dashboard editor and auth screens. */
import { BRAND, BRAND_SHADOW, BRAND_SHADOW_HOVER } from '../../theme/tokens';

export const getDarkTextPalette = () => ({
  primary: '#F1F5F9',
  secondary: 'rgba(241, 245, 249, 0.70)',
  disabled: 'rgba(241, 245, 249, 0.45)',
});

export const getLightTextPalette = () => ({
  primary: '#111827',
  secondary: 'rgba(17, 24, 39, 0.64)',
  disabled: 'rgba(17, 24, 39, 0.38)',
});

export const getFormFieldThemeOverrides = (mode) => {
  const isDark = mode === 'dark';
  const focusColor = isDark ? BRAND.primaryLight : BRAND.primary;
  const labelColor = isDark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)';
  const labelShrinkColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)';
  const helperColor = isDark ? 'rgba(255,255,255,0.58)' : 'rgba(0,0,0,0.55)';
  const inputTextColor = isDark ? '#f5f5f5' : '#1a1a1a';
  const placeholderColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)';
  const inputBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.02)';
  const borderColor = isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)';
  const borderHover = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.28)';

  return {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: inputBg,
          color: inputTextColor,
          '& fieldset': { borderColor },
          '&:hover fieldset': { borderColor: borderHover },
          '&.Mui-focused fieldset': { borderColor: focusColor },
        },
        input: {
          color: inputTextColor,
          '&::placeholder': {
            color: placeholderColor,
            opacity: 1,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: inputTextColor,
          '&::placeholder': {
            color: placeholderColor,
            opacity: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: labelColor,
          '&.Mui-focused': { color: focusColor },
          '&.MuiInputLabel-shrink': { color: labelShrinkColor },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { color: helperColor },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          color: inputTextColor,
        },
        icon: {
          color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.54)',
        },
      },
    },
  };
};

export const formSectionSx = (emphasized = false) => (theme) => ({
  p: 2,
  mb: 2,
  borderRadius: 2.5,
  border: '1px solid',
  borderColor: 'divider',
  position: 'relative',
  overflow: 'hidden',
  bgcolor:
    theme.palette.mode === 'dark'
      ? emphasized
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(255,255,255,0.02)'
      : emphasized
        ? 'rgba(99, 102, 241, 0.03)'
        : 'background.paper',
  boxShadow:
    theme.palette.mode === 'dark'
      ? 'none'
      : emphasized
        ? '0 4px 20px rgba(15, 23, 42, 0.05)'
        : '0 2px 12px rgba(15, 23, 42, 0.04)',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 12,
    bottom: 12,
    width: 3,
    borderRadius: '0 4px 4px 0',
    background: emphasized ? BRAND.gradient : 'transparent',
    opacity: emphasized ? 1 : 0,
  },
});

export const formSectionTitleSx = {
  display: 'block',
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  fontSize: '0.68rem',
  color: 'text.secondary',
  mb: 1.5,
};

export const generateButtonSx = {
  height: 52,
  borderRadius: 2.5,
  fontWeight: 700,
  fontSize: '0.95rem',
  color: '#fff',
  background: BRAND.gradient,
  boxShadow: BRAND_SHADOW,
  transition: 'transform 0.15s ease, box-shadow 0.2s ease',
  '&:hover': {
    background: BRAND.gradientHover,
    boxShadow: BRAND_SHADOW_HOVER,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    background: BRAND.gradient,
    color: 'rgba(255,255,255,0.85)',
    opacity: 0.55,
  },
};

/** Sticky footer strip for mobile editor actions */
export const mobileStickyFooterSx = (theme) => ({
  flexShrink: 0,
  p: 2,
  pt: 1.5,
  borderTop: '1px solid',
  borderColor: 'divider',
  bgcolor: theme.palette.background.default,
  boxShadow: theme.palette.mode === 'dark' ? '0 -8px 24px rgba(0,0,0,0.45)' : '0 -4px 16px rgba(15,23,42,0.08)',
});

export const mobileSegmentedControlSx = (theme) => ({
  display: 'flex',
  p: 0.5,
  borderRadius: 2.5,
  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
});

export const mobileSegmentButtonSx = (active) => (theme) => ({
  flex: 1,
  py: 1,
  minHeight: 40,
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8125rem',
  color: active ? '#fff' : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.6)',
  background: active ? BRAND.gradient : 'transparent',
  boxShadow: active ? '0 2px 10px rgba(99, 102, 241, 0.35)' : 'none',
  '& .MuiButton-startIcon': {
    marginRight: 0.75,
    '& svg': { fontSize: 17 },
  },
  '&:hover': {
    background: active ? BRAND.gradientHover : 'action.hover',
  },
});

export const mobilePreviewSelectSx = {
  height: 34,
  borderRadius: 17,
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'text.primary',
  bgcolor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiSelect-select': { py: 0.75, pl: 1.5, pr: 3, color: 'text.primary' },
  '& .MuiSelect-icon': {
    color: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.54)'),
  },
};

/** Desktop preview toolbar selects (color theme, font). */
export const previewToolbarSelectSx = {
  height: 36,
  borderRadius: 2,
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'text.primary',
  bgcolor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'divider',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
    borderWidth: 1,
  },
  '& .MuiSelect-select': {
    py: 0.75,
    pl: 1.25,
    pr: 3,
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-icon': {
    color: 'text.secondary',
  },
};

export const mobileZoomGroupSx = (theme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 2,
  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
  border: '1px solid',
  borderColor: 'divider',
});

/** Subtle mesh background for dashboard main areas */
export const dashboardShellBgSx = (theme) => ({
  background:
    theme.palette.mode === 'dark'
      ? `radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99,102,241,0.14) 0%, transparent 55%),
         radial-gradient(ellipse 60% 40% at 100% 0%, rgba(139,92,246,0.08) 0%, transparent 50%),
         ${theme.palette.background.default}`
      : `radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99,102,241,0.09) 0%, transparent 55%),
         radial-gradient(ellipse 60% 40% at 100% 0%, rgba(139,92,246,0.06) 0%, transparent 50%),
         ${theme.palette.background.default}`,
});

/** Glass-style app bar */
export const appBarSx = (theme) => ({
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor:
    theme.palette.mode === 'dark'
      ? 'rgba(17, 26, 46, 0.88)'
      : 'rgba(255, 255, 255, 0.88)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 0 rgba(15,23,42,0.04)',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 2,
    background: BRAND.gradient,
    opacity: theme.palette.mode === 'dark' ? 0.35 : 0.5,
  },
});

/** Preview toolbar strip */
export const previewToolbarSx = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: { xs: 1.5, sm: 2 },
  py: 1,
  minHeight: 52,
  bgcolor:
    theme.palette.mode === 'dark'
      ? 'rgba(17, 26, 46, 0.92)'
      : 'rgba(255, 255, 255, 0.92)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid',
  borderColor: 'divider',
});

/** Visual template picker card (editor + preview toolbar) */
export const templatePickerCardSx = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  width: '100%',
  p: 1,
  pr: 1.5,
  borderRadius: 2.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor:
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'background.paper',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.15s ease',
  '&:hover': {
    borderColor: 'primary.main',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 20px rgba(0,0,0,0.35)'
      : '0 6px 20px rgba(99, 102, 241, 0.12)',
    transform: 'translateY(-1px)',
  },
});

export const templatePickerThumbSx = {
  width: 48,
  flexShrink: 0,
  borderRadius: 1,
  overflow: 'hidden',
};

/** Shared tab strip for dashboard forms */
export const formTabsSx = {
  borderBottom: 1,
  borderColor: 'divider',
  minHeight: 48,
  '& .MuiTab-root': {
    minHeight: 48,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
};

/** Centered dashed drop zone for file uploads */
export const fileDropZoneSx = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  width: '100%',
  py: 3.5,
  px: 2.5,
  borderRadius: 3,
  border: '2px dashed',
  borderColor:
    theme.palette.mode === 'dark' ? 'rgba(129, 140, 248, 0.4)' : 'rgba(99, 102, 241, 0.35)',
  background:
    theme.palette.mode === 'dark'
      ? `linear-gradient(160deg, rgba(99,102,241,0.1) 0%, rgba(17,26,46,0.4) 100%)`
      : `linear-gradient(160deg, rgba(99,102,241,0.07) 0%, rgba(255,255,255,0.9) 100%)`,
  cursor: 'pointer',
  textAlign: 'center',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
  '&:hover': {
    borderColor: 'primary.main',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 10px 28px rgba(0,0,0,0.35)'
        : '0 10px 28px rgba(99, 102, 241, 0.16)',
    transform: 'translateY(-2px)',
  },
});

export const fileDropZoneIconSx = {
  width: 52,
  height: 52,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: BRAND.gradient,
  color: '#fff',
  boxShadow: BRAND_SHADOW,
  mb: 0.5,
};

/** Compact card shown after a file is selected */
export const uploadedFileCardSx = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  width: '100%',
  p: 1.5,
  borderRadius: 2.5,
  border: '1px solid',
  borderColor: 'primary.main',
  bgcolor:
    theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.06)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 16px rgba(0,0,0,0.25)'
      : '0 4px 16px rgba(99, 102, 241, 0.1)',
});
